import React from 'react';
import { Navigate } from 'react-router-dom';
import ROUTES_PATH from '@/router/routesPathConfig';

import AppRoute from '@/pages';
import RestrictedRoute from '@/pages/RestrictedRoute';
import RouteGate from '@/router/RouteGate';
import FirstOpenChildRedirect from '@/router/FirstOpenChildRedirect';
import { companySettingsLanding } from '@/pages/configurations/company/landing';
import { companySettingsDestinationRoute } from '@/pages/configurations/company/destinationRoute';
import { companyBankFeedDestinationRoute } from '@/pages/company/BankFeedRoute/destinationRoute';
import { companyBankFeedLanding } from '@/pages/company/BankFeedRoute/landing';
import { companyDashboardDestinationRoute } from '@/pages/company/DashboardRoute/destinationRoute';
import { companyWorkspaceDestinationRoute } from '@/pages/company/WorkspaceRoute/destinationRoute';
import { companyDashboardLanding } from '@/pages/company/DashboardRoute/landing';
import { companyRoute } from '@/pages/company/companyRoute';
import { companySettingsRootRoute } from '@/abilities/companySettings/routes';
import { companyWorkspaceRoute } from '@/abilities/companyWorkspace/routes';
import { toCompanySettingsDestination } from '@/abilities/companySettings/vocabulary';
import RestrictedRouteForPay from '@/pages/RestrictedRouteForPay';
import RedirectRoute from '@/pages/redirect';
import ExpiredRoute from '@/pages/expired';
import Route404 from '@/pages/servicePage/page404';
import DocumentNotFoundRoute from '@/pages/servicePage/documentNotFound';
import BrowserNotSupportedRoute from '@/pages/notSupported';
import IpadNotSupportedRoute from '@/pages/notSupported/ipad';
import MobileBrowserNotSupportedRoute from '@/pages/notSupported/mobile';
import DeferredTaskRoute from '@/pages/deferredTask';

import IDPRoute from '@/pages/idp';

import CompaniesRoute from '@/pages/companies';

import CompanyWorkspaceStandardRoute from '@/pages/company/WorkspaceRoute/standard';
import CompanyWorkspaceConfidentialRoute from '@/pages/company/WorkspaceRoute/confidential';
import CompanyDashboardRoute from '@/pages/company/DashboardRoute';
import CompanyDashboardVendorExpensesRoute from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute';
import CompanyBankFeedRoute from '@/pages/company/BankFeedRoute';
import CompanyBankFeedBankTransactionsRoute from '@/pages/company/BankFeedRoute/pages/BankTransactionsRoute';
import CompanyBankFeedCreditCardTransactionsRoute from '@/pages/company/BankFeedRoute/pages/CreditCardTransactionsRoute';
// DOKKA-PAY ROUTES
import PayCompanyRoute from '@/pages/dokkaPay/pages/company';
import PayPaymentRoute from '@/pages/dokkaPay/pages/payment';
import PayConfigurationsCompanyRoute from '@/pages/dokkaPay/pages/configurations/company';
import PayCompanyProvidersRoute from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders';
import BeneficiariesRoute from '@/pages/dokkaPay/pages/configurations/company/pages/beneficiaries';

import ErrorElement from '@/lib/errorLogger/ErrorElement';

import LinearProgress from '@mui/material/LinearProgress';

const CompanyRoute = React.lazy(() => import('@/pages/company'));
const DocumentRoute = React.lazy(() => import('@/pages/document'));

// Configuration routes - lazy loaded
const OrganizationsRoute = React.lazy(() => import('@/pages/organizations'));
const ConfigurationsOrganizationRoute = React.lazy(() => import('@/pages/configurations/organization'));
const OrganizationGeneralRoute = React.lazy(() => import('@/pages/configurations/organization/pages/General'));
const OrganizationGeneralMainRoute = React.lazy(
  () => import('@/pages/configurations/organization/pages/General/Tabs/Main'),
);
const OrganizationGeneralContactRoute = React.lazy(
  () => import('@/pages/configurations/organization/pages/General/Tabs/Contact'),
);
const OrganizationGeneralFeaturesRoute = React.lazy(
  () => import('@/pages/configurations/organization/pages/General/Tabs/Features'),
);
const OrganizationBillingsRoute = React.lazy(() => import('@/pages/configurations/organization/pages/billings'));
const OrganizationConnectionsRoute = React.lazy(() => import('@/pages/configurations/organization/pages/Users'));
const OrganizationSecurityRoute = React.lazy(() => import('@/pages/configurations/organization/pages/Security'));
const OrganizationBackupsRoute = React.lazy(() => import('@/pages/configurations/organization/pages/backups'));
const OrganizationKeysRoute = React.lazy(() => import('@/pages/configurations/organization/pages/keys'));

const ConfigurationsCompanyRoute = React.lazy(() => import('@/pages/configurations/company'));
const CompanyGeneralRoute = React.lazy(() => import('@/pages/configurations/company/pages/General'));
const CompanyGeneralMainRoute = React.lazy(() => import('@/pages/configurations/company/pages/General/Tabs/Main'));
const CompanyGeneralFeaturesRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/General/Tabs/Features'),
);
const CompanyUsersRoute = React.lazy(() => import('@/pages/configurations/company/pages/users'));
const CompanyIntegrationsRoute = React.lazy(() => import('@/pages/configurations/company/pages/Integrations'));
const CompanyApplicationsRoute = React.lazy(() => import('@/pages/configurations/company/pages/applications'));
const CompanyVendorPreferencesRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/vendorPreferences'),
);
const GeneralVendorApprovalManagement = React.lazy(
  () => import('@/pages/configurations/company/pages/vendorPreferences/pages/generalManagement'),
);
const PolicyVendorApprovalManagement = React.lazy(
  () => import('@/pages/configurations/company/pages/vendorPreferences/pages/policyManagement'),
);
const CompanyApprovalsRoute = React.lazy(() => import('@/pages/configurations/company/pages/approvals'));
const CompanyApprovalsFeaturesRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/approvals/pages/ApprovalsFeatures'),
);
const CompanyApprovalsGroupsRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/approvals/pages/groups'),
);
const CompanyApprovalsApproversRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/approvals/pages/approvers'),
);
const CompanyApprovalsAdvancedGroupsRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/approvals/pages/advancedGroups'),
);
const CompanyVendorBasedApprovalRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/approvals/pages/vendorBasedApproval'),
);
const ConfigurationsCompanyBankFeedRoute = React.lazy(() => import('@/pages/configurations/company/pages/BankFeed'));
const CompanyBankFeedConnectionsRoute = React.lazy(
  () => import('@/pages/configurations/company/pages/BankFeed/pages/Connections'),
);
const CompanyBankFeedUsersRoute = React.lazy(() => import('@/pages/configurations/company/pages/BankFeed/pages/Users'));
const CompanyBankFeedSyncRoute = React.lazy(() => import('@/pages/configurations/company/pages/BankFeed/pages/Sync'));

const ProfileRoute = React.lazy(() => import('@/pages/profiles/company'));
const ProfileMainRoute = React.lazy(() => import('@/pages/profiles/company/pages/Main'));
const ProfileNotificationsRoute = React.lazy(() => import('@/pages/profiles/company/pages/notifications'));
const ProfilePreferencesRoute = React.lazy(() => import('@/pages/profiles/company/pages/preferences'));

const MobileApprovalsRoute = React.lazy(() => import('@/pages/mobile/approvals'));

// Storybook — component state showcases, lazy loaded
const ChatStorybookPage = React.lazy(
  () => import('@/components/mui/Layouts/components/Chat/components/__showcase__/ChatStorybookPage'),
);
const ColorMismatchPage = React.lazy(
  () => import('@/components/mui/Layouts/components/Chat/components/__showcase__/ColorMismatchPage'),
);

import {
  waitDocumentMobileApprovalsRouteQueries,
  waitDocumentRouteQueries,
} from '@/domain/router/waitRoutesQueries';

const routes = [
  {
    element: <AppRoute />,
    errorElement: <ErrorElement />,
    children: [
      {
        id: ROUTES_PATH.COMPANIES.id,
        path: ROUTES_PATH.COMPANIES.relative,
        index: true,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.COMPANIES.restriction}>
            <CompaniesRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DOCUMENT_NOT_FOUND.id,
        path: ROUTES_PATH.DOCUMENT_NOT_FOUND.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DOCUMENT_NOT_FOUND.restriction}>
            <DocumentNotFoundRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_IPAD.id,
        path: ROUTES_PATH.NOT_SUPPORTED_IPAD.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_IPAD.restriction}>
            <IpadNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_BROWSER.id,
        path: ROUTES_PATH.NOT_SUPPORTED_BROWSER.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_BROWSER.restriction}>
            <BrowserNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.id,
        path: ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.restriction}>
            <MobileBrowserNotSupportedRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.AUTH_IDP_LOGIN.id,
        path: ROUTES_PATH.AUTH_IDP_LOGIN.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.AUTH_IDP_LOGIN.restriction}>
            <IDPRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.AUTH_LOGIN.id,
        path: ROUTES_PATH.AUTH_LOGIN.relative,
        element: <Navigate to={ROUTES_PATH.COMPANIES.relative} replace />,
      },
      {
        id: ROUTES_PATH.REDIRECT.id,
        path: ROUTES_PATH.REDIRECT.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.REDIRECT.restriction}>
            <RedirectRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DEFERRED_TASK.id,
        path: ROUTES_PATH.DEFERRED_TASK.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.DEFERRED_TASK.restriction}>
            <DeferredTaskRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.id,
        path: ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.relative,
        element: (
          <RestrictedRoute
            waitForQueries={waitDocumentMobileApprovalsRouteQueries}
            restriction={ROUTES_PATH.DOCUMENT_MOBILE_APPROVALS.restriction}
          >
            <MobileApprovalsRoute />
          </RestrictedRoute>
        ),
      },

      {
        id: ROUTES_PATH.ORGANIZATIONS.id,
        path: ROUTES_PATH.ORGANIZATIONS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATIONS.restriction}>
            <OrganizationsRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.DOCUMENT.id,
        path: ROUTES_PATH.DOCUMENT.relative,
        element: (
          <RestrictedRoute waitForQueries={waitDocumentRouteQueries} restriction={ROUTES_PATH.DOCUMENT.restriction}>
            <DocumentRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.id,
        path: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.relative,
        element: (
          <RouteGate
            key={ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.id}
            canOpen={companyWorkspaceRoute('standard')}
          >
            <LinearProgress sx={{ width: '100%' }} />
          </RouteGate>
        ),
      },
      {
        id: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.id,
        path: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.relative,
        element: (
          <RouteGate
            key={ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.id}
            canOpen={companyWorkspaceRoute('confidential')}
          >
            <LinearProgress sx={{ width: '100%' }} />
          </RouteGate>
        ),
      },
      {
        id: ROUTES_PATH.COMPANY.id,
        path: ROUTES_PATH.COMPANY.relative,
        element: (
          <RouteGate key={ROUTES_PATH.COMPANY.id} canOpen={companyRoute}>
            <CompanyRoute />
          </RouteGate>
        ),
        children: [
          companyWorkspaceDestinationRoute('standard', <CompanyWorkspaceStandardRoute />),
          companyWorkspaceDestinationRoute('confidential', <CompanyWorkspaceConfidentialRoute />),
          {
            ...companyDashboardDestinationRoute('root', <CompanyDashboardRoute />),
            children: [
              { index: true, element: <FirstOpenChildRedirect landing={companyDashboardLanding('root')} /> },
              companyDashboardDestinationRoute('vendorExpenses', <CompanyDashboardVendorExpensesRoute />),
            ],
          },
          {
            ...companyBankFeedDestinationRoute('root', <CompanyBankFeedRoute />),
            children: [
              { index: true, element: <FirstOpenChildRedirect landing={companyBankFeedLanding('root')} /> },
              companyBankFeedDestinationRoute('transactions', <CompanyBankFeedBankTransactionsRoute />),
              companyBankFeedDestinationRoute('creditCards', <CompanyBankFeedCreditCardTransactionsRoute />),
            ],
          },
        ],
      },
      // ORGANIZATION SETTINGS
      {
        id: ROUTES_PATH.ORGANIZATION_SETTINGS.id,
        path: ROUTES_PATH.ORGANIZATION_SETTINGS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS.restriction}>
            <ConfigurationsOrganizationRoute />
          </RestrictedRoute>
        ),
        children: [
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_BILLINGS.restriction}>
                <OrganizationBillingsRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_USERS.restriction}>
                <OrganizationConnectionsRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_SECURITY.restriction}>
                <OrganizationSecurityRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_BACKUPS.restriction}>
                <OrganizationBackupsRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL.restriction}>
                <OrganizationGeneralRoute />
              </RestrictedRoute>
            ),
            children: [
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_MAIN.restriction}>
                    <OrganizationGeneralMainRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_CONTACT.restriction}>
                    <OrganizationGeneralContactRoute />
                  </RestrictedRoute>
                ),
              },
              {
                id: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.id,
                path: ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.relative,
                element: (
                  <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_GENERAL_FEATURES.restriction}>
                    <OrganizationGeneralFeaturesRoute />
                  </RestrictedRoute>
                ),
              },
            ],
          },
          {
            id: ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.id,
            path: ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.ORGANIZATION_SETTINGS_API_KEYS.restriction}>
                <OrganizationKeysRoute />
              </RestrictedRoute>
            ),
          },
        ],
      },
      // COMPANY SETTINGS
      {
        id: ROUTES_PATH.COMPANY_SETTINGS.id,
        path: ROUTES_PATH.COMPANY_SETTINGS.relative,
        element: (
          <RouteGate key={ROUTES_PATH.COMPANY_SETTINGS.id} canOpen={companySettingsRootRoute}>
            <ConfigurationsCompanyRoute />
          </RouteGate>
        ),
        children: [
          {
            index: true,
            handle: toCompanySettingsDestination('root'),
            element: <FirstOpenChildRedirect landing={companySettingsLanding('root')} />,
          },
          {
            ...companySettingsDestinationRoute('general', <CompanyGeneralRoute />),
            children: [
              {
                index: true,
                element: <FirstOpenChildRedirect landing={companySettingsLanding('general')} />,
              },
              companySettingsDestinationRoute('general.main', <CompanyGeneralMainRoute />),
              companySettingsDestinationRoute('general.features', <CompanyGeneralFeaturesRoute />),
            ],
          },
          companySettingsDestinationRoute('users', <CompanyUsersRoute />),
          companySettingsDestinationRoute('integrations', <CompanyIntegrationsRoute />),
          companySettingsDestinationRoute('applications', <CompanyApplicationsRoute />),
          {
            ...companySettingsDestinationRoute('vendorPreferences', <CompanyVendorPreferencesRoute />),
            children: [
              {
                index: true,
                element: <FirstOpenChildRedirect landing={companySettingsLanding('vendorPreferences')} />,
              },
              companySettingsDestinationRoute('vendorPreferences.general', <GeneralVendorApprovalManagement />),
              companySettingsDestinationRoute('vendorPreferences.policies', <PolicyVendorApprovalManagement />),
            ],
          },
          {
            ...companySettingsDestinationRoute('approvals', <CompanyApprovalsRoute />),
            children: [
              {
                index: true,
                element: <FirstOpenChildRedirect landing={companySettingsLanding('approvals')} />,
              },
              companySettingsDestinationRoute('approvals.approvers', <CompanyApprovalsApproversRoute />),
              companySettingsDestinationRoute('approvals.basicGroups', <CompanyApprovalsGroupsRoute />),
              companySettingsDestinationRoute('approvals.advancedGroups', <CompanyApprovalsAdvancedGroupsRoute />),
              companySettingsDestinationRoute('approvals.vendorBased', <CompanyVendorBasedApprovalRoute />),
              companySettingsDestinationRoute('approvals.features', <CompanyApprovalsFeaturesRoute />),
            ],
          },
          {
            ...companySettingsDestinationRoute('bankFeed', <ConfigurationsCompanyBankFeedRoute />),
            children: [
              {
                index: true,
                element: <FirstOpenChildRedirect landing={companySettingsLanding('bankFeed')} />,
              },
              companySettingsDestinationRoute('bankFeed.connections', <CompanyBankFeedConnectionsRoute />),
              companySettingsDestinationRoute('bankFeed.usersAndPermissions', <CompanyBankFeedUsersRoute />),
              companySettingsDestinationRoute('bankFeed.synchronization', <CompanyBankFeedSyncRoute />),
            ],
          },
        ],
      },
      // ACCOUNT SETTINGS
      {
        id: ROUTES_PATH.PROFILE_SETTINGS.id,
        path: ROUTES_PATH.PROFILE_SETTINGS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS.restriction}>
            <ProfileRoute />
          </RestrictedRoute>
        ),
        children: [
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_MAIN.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_MAIN.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_MAIN.restriction}>
                <ProfileMainRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_NOTIFICATIONS.restriction}>
                <ProfileNotificationsRoute />
              </RestrictedRoute>
            ),
          },
          {
            id: ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.id,
            path: ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.relative,
            element: (
              <RestrictedRoute restriction={ROUTES_PATH.PROFILE_SETTINGS_PREFERENCES.restriction}>
                <ProfilePreferencesRoute />
              </RestrictedRoute>
            ),
          },
        ],
      },
      // STORYBOOK — component state showcases
      {
        id: ROUTES_PATH.STORYBOOK_CHAT.id,
        path: ROUTES_PATH.STORYBOOK_CHAT.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.STORYBOOK_CHAT.restriction}>
            <ChatStorybookPage />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.STORYBOOK_CHAT_COLORS.id,
        path: ROUTES_PATH.STORYBOOK_CHAT_COLORS.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.STORYBOOK_CHAT_COLORS.restriction}>
            <ColorMismatchPage />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.EXPIRED.id,
        path: ROUTES_PATH.EXPIRED.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.EXPIRED.restriction}>
            <ExpiredRoute />
          </RestrictedRoute>
        ),
      },
      {
        id: ROUTES_PATH.PAGE404.id,
        path: ROUTES_PATH.PAGE404.relative,
        element: (
          <RestrictedRoute restriction={ROUTES_PATH.PAGE404.restriction}>
            <Route404 />
          </RestrictedRoute>
        ),
      },
      // PAY MODULE
      {
        id: ROUTES_PATH.PAY_COMPANY.id,
        path: ROUTES_PATH.PAY_COMPANY.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY.restriction}>
            <PayCompanyRoute />
          </RestrictedRouteForPay>
        ),
      },
      {
        id: ROUTES_PATH.PAY_PAYMENT.id,
        path: ROUTES_PATH.PAY_PAYMENT.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_PAYMENT.restriction}>
            <PayPaymentRoute />
          </RestrictedRouteForPay>
        ),
      },
      // PAY COMPANY SETTINGS
      {
        id: ROUTES_PATH.PAY_COMPANY_SETTINGS.id,
        path: ROUTES_PATH.PAY_COMPANY_SETTINGS.relative,
        element: (
          <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS.restriction}>
            <PayConfigurationsCompanyRoute />
          </RestrictedRouteForPay>
        ),
        children: [
          {
            id: ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.id,
            path: ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.relative,
            element: (
              <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.restriction}>
                <PayCompanyProvidersRoute />
              </RestrictedRouteForPay>
            ),
          },
          {
            id: ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.id,
            path: ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.relative,
            element: (
              <RestrictedRouteForPay restriction={ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.restriction}>
                <BeneficiariesRoute />
              </RestrictedRouteForPay>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;
